// React
import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { Stage, Layer, Image, Line, Circle } from 'react-konva';
import { useWatch } from 'react-hook-form';
// material-ui components
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
// custom components
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';

const arraysMatch = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((value, index) => value === arr2[index]);
};

export default function PECoordinates({
  control,
  errors,
  setValue,
  imageName,
  selectedPovIndex
}) {
  const [targetObjects, setTargetObjects] = useState([]);
  const [activeObject, setActiveObject] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [stageWidth, setStageWidth] = useState(800);
  const [stageHeight, setStageHeight] = useState(450);
  const containerRef = useRef(null);

  const ORIGINAL_WIDTH = 1920; // The original width of the image
  const ORIGINAL_HEIGHT = 1080; // The original height of the image

  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });

  const [drawnShapes, setDrawnShapes] = useState([]);

  const updateStageSize = () => {
    if (containerRef.current) {
      const width = Math.max(containerRef.current.clientWidth, 400);
      const height = (width * 9) / 16;
      setStageWidth(width);
      setStageHeight(height);
    }
  };

  useEffect(() => {
    const storedShapes =
      control._formValues?.content?.povs?.[selectedPovIndex]?.targets || [];

    if (storedShapes.length > 0 && drawnShapes.length === 0) {
      setDrawnShapes(storedShapes);
    }
  }, [selectedPovIndex, control]);

  useLayoutEffect(() => {
    updateStageSize(); // Initial size attempt

    // 🔹 ResizeObserver: Detects container size changes dynamically
    const resizeObserver = new ResizeObserver(updateStageSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // 🔹 MutationObserver: Detects when a hidden parent becomes visible
    const observer = new MutationObserver(() => {
      if (containerRef.current && containerRef.current.clientWidth > 0) {
        updateStageSize();
      }
    });

    // Observe all parent elements for visibility changes
    let parent = containerRef.current;
    while (parent) {
      observer.observe(parent, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
      parent = parent.parentElement;
    }

    return () => {
      resizeObserver.disconnect();
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!imageName) return;

    // Match patterns like "ad_m01_lpe_1_render_1.png"
    const prefixMatch = imageName.match(/^([a-zA-Z]+)_m(\d{2})/);
    if (!prefixMatch) return; // Ensure it matches expected format

    const prefix = prefixMatch[1].toUpperCase(); // "AD", "RU", "KP", etc.
    const moduleNum = `M${prefixMatch[2]}`; // Convert "m01" to "M01"

    const formattedName = `${prefix}-${moduleNum}`; // "AD-M01", "RU-M03", etc.

    const img = new window.Image();
    const tryJpg = () => {
      img.src = `https://base-media-repo.web.app/${prefix}/${formattedName}/images/paths/${imageName}.jpg`;
      img.onerror = () => console.error('Neither PNG nor JPG found.');
    };

    img.src = `https://base-media-repo.web.app/${prefix}/${formattedName}/images/paths/${imageName}.png`;

    img.onload = () => setBackgroundImage(img);
    img.onerror = tryJpg;
  }, [imageName]);

  const watchedContentCore = useWatch({
    control,
    name: 'content.core'
  });

  const COLORS = [
    '#e6194b',
    '#3cb44b',
    '#ffe119',
    '#4363d8',
    '#f58231',
    '#911eb4',
    '#46f0f0',
    '#f032e6',
    '#bcf60c',
    '#fabebe'
  ];

  const handlePointDrag = (id, pointIndex, newX, newY) => {
    setDrawnShapes((prevShapes) => {
      return prevShapes.map((shape) => {
        if (shape.id === id) {
          const newPoints = [...shape.points];
          newPoints[pointIndex] = { x: newX, y: newY };
          return { ...shape, points: newPoints };
        }
        return shape;
      });
    });
  };

  const handlePointDelete = (id, pointIndex) => {
    setDrawnShapes((prevShapes) => {
      return prevShapes
        .map((shape) => {
          if (shape.id === id) {
            const newPoints = shape.points.filter(
              (_, index) => index !== pointIndex
            );
            return { ...shape, points: newPoints };
          }
          return shape;
        })
        .filter((shape) => shape.points.length > 0); // Remove empty shapes
    });
  };

  useEffect(() => {
    if (!watchedContentCore || watchedContentCore.length === 0) return;

    // this will be used to show a count on each button
    const idCountMap = new Map();
    watchedContentCore.forEach((item) => {
      const obj = item?.object;
      if (obj?.id) {
        idCountMap.set(obj.id, (idCountMap.get(obj.id) || 0) + 1);
      }
    });

    // this will create 1 unique button for each object
    const uniqueObjectsMap = new Map();
    watchedContentCore.forEach((item, index) => {
      const obj = item?.object;
      if (obj?.id && !uniqueObjectsMap.has(obj.id)) {
        const count = idCountMap.get(obj.id);
        const labelSuffix = count > 1 ? ` (${count})` : '';
        uniqueObjectsMap.set(obj.id, {
          ...obj,
          color: COLORS[uniqueObjectsMap.size % COLORS.length],
          label: `${obj.label || `Object ${index + 1}`}${labelSuffix}`
        });
      }
    });

    const objects = Array.from(uniqueObjectsMap.values());

    if (!arraysMatch(objects, targetObjects)) {
      setTargetObjects(objects);

      // Remove lines for deleted objects
      setDrawnShapes((prevShapes) => {
        return objects.map((obj) => {
          const existingShape = prevShapes.find((shape) => shape.id === obj.id);
          return existingShape ? existingShape : { id: obj.id, points: [] };
        });
      });
    }
  }, [watchedContentCore, setValue]);

  useEffect(() => {
    setValue(`content.povs.${selectedPovIndex}.targets`, drawnShapes, {
      shouldDirty: true,
      shouldValidate: true
    });
  }, [drawnShapes]);

  const MultiPointLine = ({
    points,
    color,
    closed,
    onPointDrag,
    onPointDelete
  }) => {
    return (
      <>
        {/* Draw the line */}
        <Line
          points={points.flatMap(({ x, y }) => [
            (x / ORIGINAL_WIDTH) * stageWidth,
            (y / ORIGINAL_HEIGHT) * stageHeight
          ])}
          stroke={color}
          strokeWidth={2 / scale}
          closed={closed}
          fill={closed ? `${color}80` : 'transparent'}
          onClick={(e) => {
            e.cancelBubble = true; // Prevents the click from propagating to other elements
          }}
        />

        {/* Render draggable points */}
        {points.map((point, index) => (
          <Circle
            key={index}
            x={(point.x / ORIGINAL_WIDTH) * stageWidth}
            y={(point.y / ORIGINAL_HEIGHT) * stageHeight}
            radius={5 / scale}
            fill={color}
            stroke={index === points.length - 1 ? 'black' : 'transparent'}
            strokeWidth={index === points.length - 1 ? 1 : 0}
            draggable
            onDragMove={(e) => {
              e.target.setAttrs({
                x: e.target.x(),
                y: e.target.y()
              });
            }}
            onDragEnd={(e) => {
              onPointDrag(
                index,
                (e.target.x() / stageWidth) * ORIGINAL_WIDTH,
                (e.target.y() / stageHeight) * ORIGINAL_HEIGHT
              );
            }}
            onContextMenu={(e) => {
              e.evt.preventDefault();
              onPointDelete(index);
            }}
          />
        ))}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', maxWidth: '1200px', margin: 'auto' }}
    >
      <NumberedHeading
        number="-"
        title="Place Hotspots"
        instructions="Choose an Object to edit it's selectable region. Then left click anywhere you want to place a point, once 3 or more points exist it will auto create a shape with a clickable region for the user. Dots can be deleted with right click or moved with a left click and drag. You can add as many points as necessary but either go clockwise or counter clockwise to create a valid shape. Stage can be zoomed with mouse wheel in/out and stage can be moved with a left click and drag anywhere that is not a already placed point for a shape."
      />
      {targetObjects.map((obj, index) => (
        <Button
          key={index}
          variant={obj.id === activeObject?.id ? 'contained' : 'outlined'}
          onClick={() => setActiveObject(obj)}
          sx={{
            margin: 1,
            backgroundColor:
              obj.id === activeObject?.id ? obj.color : 'transparent',
            color: obj.id === activeObject?.id ? 'white' : obj.color,
            border: `2px solid ${obj.color}`,
            fontWeight: obj.id === activeObject?.id ? 'bold' : 'normal',
            '&:hover': {
              backgroundColor: obj.color,
              color: 'white'
            }
          }}
        >
          {obj.label || `Object ${index + 1}`}
        </Button>
      ))}

      {activeObject && <p>Active Object ID: {activeObject.id}</p>}

      <Divider className="my-16" />
      <Button
        variant="outlined"
        sx={{ margin: 1 }}
        onClick={() => {
          setScale(1);
          setStagePos({ x: 0, y: 0 });
        }}
      >
        Reset Stage Scale
      </Button>

      <Stage
        width={stageWidth}
        height={stageHeight}
        draggable
        scaleX={scale}
        scaleY={scale}
        x={stagePos.x}
        y={stagePos.y}
        onDragEnd={(e) => {
          // Only update stagePos if the drag event comes from the stage itself
          if (e.target.getClassName() === 'Stage') {
            setStagePos({ x: e.target.x(), y: e.target.y() });
          }
        }}
        onWheel={(e) => {
          e.evt.preventDefault();
          const stage = e.target.getStage();
          const oldScale = stage.scaleX();
          const pointer = stage.getPointerPosition();

          const scaleBy = 1.05;
          const newScale =
            e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

          setScale(newScale);

          // Adjust stage position to zoom around cursor
          const mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale
          };

          setStagePos({
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale
          });
        }}
        onContextMenu={(e) => {
          e.evt.preventDefault(); // Prevents the default right-click menu
        }}
        onClick={(e) => {
          if (e.evt.button === 2) return; // Ignore right-clicks

          if (!activeObject) return;

          const stage = e.target.getStage();
          const pointerPos = stage.getPointerPosition();
          if (!pointerPos) return;

          const transform = stage.getAbsoluteTransform().copy();
          transform.invert();
          const pos = transform.point(pointerPos);

          // Scale back to the original coordinate system
          const originalX = (pos.x / stageWidth) * ORIGINAL_WIDTH;
          const originalY = (pos.y / stageHeight) * ORIGINAL_HEIGHT;

          setDrawnShapes((prevShapes) => {
            if (!Array.isArray(prevShapes)) {
              prevShapes = [];
            }

            const newShapes = [...prevShapes];
            const existingShape = newShapes.find(
              (shape) => shape.id === activeObject.id
            );

            if (existingShape) {
              existingShape.points.push({ x: originalX, y: originalY });
            } else {
              newShapes.push({
                id: activeObject.id,
                points: [{ x: originalX, y: originalY }]
              });
            }

            return newShapes;
          });
        }}
      >
        <Layer>
          {backgroundImage && (
            <Image
              image={backgroundImage}
              x={0}
              y={0}
              width={stageWidth}
              height={stageHeight}
            />
          )}
          {drawnShapes.map(({ id, points }) => (
            <MultiPointLine
              key={id}
              points={points}
              color={
                targetObjects.find((obj) => obj.id === id)?.color || 'black'
              }
              closed
              onPointDrag={(pointIndex, newX, newY) =>
                handlePointDrag(id, pointIndex, newX, newY)
              }
              onPointDelete={(pointIndex) => handlePointDelete(id, pointIndex)}
            />
          ))}
        </Layer>
      </Stage>
    </div>
  );
}
